import React, { useState } from "react";
import {
    Button, Card, Form, Input, Alert, Checkbox, Row, Col,
    Typography, Flex, Steps,
} from "antd";
import {
    UserOutlined, FolderOutlined, FileTextOutlined, CheckCircleOutlined,
    MailOutlined, PhoneOutlined, IdcardOutlined, HomeOutlined, LockOutlined,
    ArrowLeftOutlined, ArrowRightOutlined, ClockCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import TopNavigation from "./snipets/TopNavigation";
import Footer from "./snipets/Footer";
import { User }                  from "../models/User";
import { Bank, BankAccount }     from "../models/Bank";
import { AuthenticationService } from "../services/AuthenticationService";

import standard_bank from "../assets/standard_bank-logo_brandlogos.net_aqqyw.png";
import capitec       from "../assets/960px-Capitec_Bank_logo.svg.png";
import fnb           from "../assets/First_National_Bank_Logo.png";
import absa          from "../assets/ABSA_Logo.svg";
import africanBank   from "../assets/African_Bank_logo.svg";
import nedbank       from "../assets/Nedbank_logo.svg";
import tyme          from "../assets/TymeBank_Logo.jpg";

const { Title, Text, Link } = Typography;

const CAPITEC_BLUE = "#2F70EF";

const BANKS = [
    { key: "capitec", dbId: 1, name: "Capitec",       recommended: true,  logo: capitec       },
    { key: "fnb",     dbId: 2, name: "FNB",           recommended: false, logo: fnb           },
    { key: "absa",    dbId: 3, name: "ABSA",          recommended: false, logo: absa          },
    { key: "std",     dbId: 4, name: "Standard Bank", recommended: false, logo: standard_bank },
    { key: "ned",     dbId: 5, name: "Nedbank",       recommended: false, logo: nedbank       },
    { key: "tyme",    dbId: 6, name: "TymeBank",      recommended: false, logo: tyme          },
    { key: "african", dbId: 7, name: "African Bank",  recommended: false, logo: africanBank   },
];

interface Documents {
    driversLicense: File | null;
    idPassport:     File | null;
    bankStatement:  File | null;
}

// ── Upload Box ────────────────────────────────────────────────────────────────

const UploadBox: React.FC<{
    label: string; file: File | null; onSelect: (f: File) => void;
}> = ({ label, file, onSelect }) => {
    const ref = React.useRef<HTMLInputElement>(null);
    return (
        <div
            onClick={() => ref.current?.click()}
            style={{
                border: `2px dashed ${file ? "#00AF66" : "#D0D0D0"}`,
                borderRadius: 14, padding: "32px 20px", textAlign: "center",
                cursor: "pointer", background: file ? "#E6F7EF" : "#FAFAFA",
            }}
        >
            <input ref={ref} type="file" accept="image/*,.pdf" style={{ display: "none" }}
                   onChange={(e) => e.target.files?.[0] && onSelect(e.target.files[0])} />
            <CheckCircleOutlined style={{ fontSize: 32, color: file ? "#00AF66" : "#BDBDBD", display: "block", marginBottom: 12 }} />
            <Text strong style={{ color: file ? "#00AF66" : "#1A1A2E", display: "block", marginBottom: 4 }}>{label}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
                {file ? file.name : "Click to upload — JPG, PNG or PDF"}
            </Text>
        </div>
    );
};

// ── Bank Card ─────────────────────────────────────────────────────────────────

const BankCard: React.FC<{
    bank: typeof BANKS[0]; selected: boolean; onSelect: () => void;
}> = ({ bank, selected, onSelect }) => (
    <div
        onClick={onSelect}
        style={{
            border: `2px solid ${selected ? "#00AF66" : bank.recommended ? CAPITEC_BLUE : "#E0E0E0"}`,
            borderRadius: 12, padding: "12px 8px", textAlign: "center",
            cursor: "pointer", background: selected ? "#E6F7EF" : "white",
            position: "relative",
        }}
    >
        {bank.recommended && (
            <div style={{
                position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)",
                background: CAPITEC_BLUE, color: "white", fontSize: 9, fontWeight: 900,
                padding: "2px 8px", borderRadius: 20, whiteSpace: "nowrap",
            }}>
                ⚡ FASTEST PAYOUT
            </div>
        )}
        <div style={{ height: 36, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 8 }}>
            <img src={bank.logo} alt={bank.name}
                 style={{ maxHeight: 32, maxWidth: "90%", objectFit: "contain" }}
                 onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
        </div>
        <Text style={{ fontSize: 11, fontWeight: selected ? 800 : 600, color: selected ? "#00AF66" : "#555" }}>
            {bank.name}
        </Text>
    </div>
);

// ── Main Page ─────────────────────────────────────────────────────────────────

const DriverRegisterPage: React.FC = () => {
    const navigate = useNavigate();

    const [step,          setStep]          = useState(0);
    const [error,         setError]         = useState("");
    const [loading,       setLoading]       = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [user,          setUser]          = useState<User>(new User());
    const [docs,          setDocs]          = useState<Documents>({ driversLicense: null, idPassport: null, bankStatement: null });

    const setU = (field: keyof User) => (e: any) => {
        const updated = Object.assign(new User(), user);
        (updated as any)[field] = e.target?.value ?? e;
        setUser(updated);
    };

    const validateStep0 = () => {
        if (!user.name || !user.surname || !user.email || !user.phone || !user.id_number || !user.address || !user.password) {
            setError("Please fill in all required fields."); return false;
        }
        if (!/\S+@\S+\.\S+/.test(user.email)) { setError("Please enter a valid email address."); return false; }
        if (user.password.length < 8)          { setError("Password must be at least 8 characters."); return false; }
        setError(""); return true;
    };

    const validateStep1 = () => {
        if (!docs.driversLicense)         { setError("Please upload your driver's license."); return false; }
        if (!docs.idPassport)             { setError("Please upload your ID or passport.");   return false; }
        if (!user.bank_account?.bank?.id) { setError("Please select your bank.");             return false; }
        setError(""); return true;
    };

    const validateStep2 = () => {
        if (!acceptedTerms) { setError("Please accept the terms and conditions to continue."); return false; }
        setError(""); return true;
    };

    const handleNext = () => {
        if (step === 0 && validateStep0()) setStep(1);
        if (step === 1 && validateStep1()) setStep(2);
    };

    const handleSubmit = async () => {
        if (!validateStep2()) return;
        setLoading(true);
        setError("");
        try {
            const licenseUpload  = await AuthenticationService.uploadFile(docs.driversLicense!);
            const idUpload       = await AuthenticationService.uploadFile(docs.idPassport!);
            const bankStmtUpload = docs.bankStatement ? await AuthenticationService.uploadFile(docs.bankStatement) : null;

            const finalUser           = Object.assign(new User(), user);
            finalUser.drivers_license = licenseUpload.url;
            finalUser.bank_statement  = bankStmtUpload?.url ?? "";

            await AuthenticationService.registerDriver(finalUser);
            setStep(3);
        } catch (err: any) {
            const s = err?.response?.status || err?.status;
            if (s === 401)      setError("Not authorized. Please log in again.");
            else if (s === 400) setError("Invalid data. Please check your inputs.");
            else if (s === 409) setError("An account with this email already exists.");
            else if (s >= 500)  setError("Server error. Please try again later.");
            else                setError("Submission failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <TopNavigation />

            <Flex align="flex-start" justify="center" style={{ minHeight: "100vh", background: "#f5f5f0", padding: "100px 24px 60px" }}>
                <div style={{ width: "100%", maxWidth: 680 }}>

                    {step < 3 && (
                        <>
                            <Title level={3} style={{ margin: "0 0 4px" }}>Driver Registration</Title>
                            <Text type="secondary" style={{ display: "block", marginBottom: 24 }}>
                                Join our delivery team in three simple steps
                            </Text>
                            <Steps
                                current={step}
                                style={{ marginBottom: 32 }}
                                items={[
                                    { title: "Personal Info",  icon: <UserOutlined /> },
                                    { title: "Documents",      icon: <FolderOutlined /> },
                                    { title: "Terms",          icon: <FileTextOutlined /> },
                                ]}
                            />
                        </>
                    )}

                    <Card>
                        {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 20 }} />}

                        {/* ── Step 0: Personal Info ── */}
                        {step === 0 && (
                            <>
                                <Title level={5} type="success" style={{ marginBottom: 24 }}>
                                    <UserOutlined /> Personal Information
                                </Title>
                                <Form layout="vertical">
                                    <Row gutter={16}>
                                        <Col xs={24} sm={12}>
                                            <Form.Item label="First Name *">
                                                <Input placeholder="First name" value={user.name} onChange={setU("name")} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={12}>
                                            <Form.Item label="Last Name *">
                                                <Input placeholder="Last name" value={user.surname} onChange={setU("surname")} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={12}>
                                            <Form.Item label="Email Address *">
                                                <Input prefix={<MailOutlined />} placeholder="email@example.com" type="email" value={user.email} onChange={setU("email")} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={12}>
                                            <Form.Item label="Phone Number *">
                                                <Input prefix={<PhoneOutlined />} placeholder="0XX XXX XXXX" type="tel" value={user.phone} onChange={setU("phone")} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24}>
                                            <Form.Item label="ID / Passport Number *">
                                                <Input prefix={<IdcardOutlined />} placeholder="ID or passport number" value={user.id_number} onChange={setU("id_number")} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24}>
                                            <Form.Item label="Street Address *">
                                                <Input prefix={<HomeOutlined />} placeholder="123 Main Street" value={user.address} onChange={setU("address")} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24}>
                                            <Form.Item label="Password *">
                                                <Input.Password prefix={<LockOutlined />} placeholder="Create a password (min 8 characters)" value={user.password} onChange={setU("password")} />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </Form>
                                <Button type="primary" size="large" block onClick={handleNext} icon={<ArrowRightOutlined />} iconPosition="end">
                                    Next — Documents &amp; Banking
                                </Button>
                            </>
                        )}

                        {/* ── Step 1: Documents & Banking ── */}
                        {step === 1 && (
                            <>
                                <Title level={5} type="success" style={{ marginBottom: 20 }}>
                                    <FolderOutlined /> Document Uploads
                                </Title>
                                <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
                                    <Col xs={24} sm={8}>
                                        <UploadBox label="Driver's License" file={docs.driversLicense}
                                                   onSelect={(f) => setDocs((d) => ({ ...d, driversLicense: f }))} />
                                    </Col>
                                    <Col xs={24} sm={8}>
                                        <UploadBox label="Passport / National ID" file={docs.idPassport}
                                                   onSelect={(f) => setDocs((d) => ({ ...d, idPassport: f }))} />
                                    </Col>
                                    <Col xs={24} sm={8}>
                                        <UploadBox label="Bank Statement (optional)" file={docs.bankStatement}
                                                   onSelect={(f) => setDocs((d) => ({ ...d, bankStatement: f }))} />
                                    </Col>
                                </Row>

                                <Title level={5} type="success" style={{ marginBottom: 12 }}>Banking Details</Title>

                                <Alert
                                    type="info"
                                    style={{ marginBottom: 20, borderLeft: `4px solid ${CAPITEC_BLUE}`, background: "#EEF4FE" }}
                                    message={<Text strong style={{ color: CAPITEC_BLUE }}>Faster Payouts with Capitec</Text>}
                                    description="We highly recommend using a Capitec Bank account — payouts are processed significantly faster compared to other banks."
                                    icon={<img src={capitec} alt="Capitec" style={{ width: 32, objectFit: "contain" }} />}
                                    showIcon
                                />

                                <Text strong style={{ display: "block", marginBottom: 12 }}>Select Your Bank *</Text>
                                <Row gutter={[12, 12]} style={{ marginBottom: 32 }}>
                                    {BANKS.map((bank) => (
                                        <Col key={bank.key} xs={12} sm={6}>
                                            <BankCard
                                                bank={bank}
                                                selected={user.bank_account?.bank?.id === bank.dbId}
                                                onSelect={() => {
                                                    const updated = Object.assign(new User(), user);
                                                    const b = new Bank(); b.id = bank.dbId; b.name = bank.name;
                                                    const acc = new BankAccount(); acc.bank = b;
                                                    setUser(updated);
                                                }}
                                            />
                                        </Col>
                                    ))}
                                </Row>

                                <Row gutter={12}>
                                    <Col xs={24} sm={8}>
                                        <Button size="large" block icon={<ArrowLeftOutlined />} onClick={() => { setError(""); setStep(0); }}>
                                            Back
                                        </Button>
                                    </Col>
                                    <Col xs={24} sm={16}>
                                        <Button type="primary" size="large" block onClick={handleNext} icon={<ArrowRightOutlined />} iconPosition="end">
                                            Next — Terms &amp; Conditions
                                        </Button>
                                    </Col>
                                </Row>
                            </>
                        )}

                        {/* ── Step 2: Terms ── */}
                        {step === 2 && (
                            <>
                                <Title level={5} type="success" style={{ marginBottom: 20 }}>
                                    <FileTextOutlined /> Terms &amp; Conditions
                                </Title>

                                <div style={{
                                    background: "#f5f5f0", borderRadius: 10, border: "1px solid #E0E0E0",
                                    padding: "16px 20px", marginBottom: 24,
                                    maxHeight: 360, overflowY: "auto",
                                    fontSize: 13, color: "#555", lineHeight: 1.8,
                                }}>
                                    <p><strong>1. Independent Contractor</strong><br />You are registered as a self-employed independent contractor and not an employee. You are responsible for your own taxes, insurance, and compliance with applicable laws.</p>
                                    <p><strong>2. Delivery Standards</strong><br />You agree to deliver orders promptly, professionally, and in accordance with our delivery guidelines. Orders must be handled with care and delivered to the correct address.</p>
                                    <p><strong>3. Earnings &amp; Payouts</strong><br />Earnings are calculated per completed delivery. Payouts are processed weekly to your registered bank account. We reserve the right to withhold payment for fraudulent or incomplete deliveries.</p>
                                    <p><strong>4. Code of Conduct</strong><br />You agree to treat customers, restaurant partners, and support staff with respect at all times. Misconduct may result in suspension or permanent removal from the platform.</p>
                                    <p><strong>5. Vehicle &amp; Equipment</strong><br />You are responsible for maintaining your own vehicle or delivery equipment in a roadworthy and safe condition. We are not liable for any vehicle-related costs or incidents.</p>
                                    <p><strong>6. Data &amp; Privacy</strong><br />Your personal information is collected and processed in accordance with our Privacy Policy and applicable South African data protection laws (POPIA).</p>
                                    <p><strong>7. Termination</strong><br />Either party may terminate this agreement at any time. We reserve the right to deactivate your account for violation of these terms without prior notice.</p>
                                </div>

                                <Checkbox checked={acceptedTerms} onChange={() => setAcceptedTerms(!acceptedTerms)} style={{ marginBottom: 24 }}>
                                    <Text strong>I have read and agree to the Driver Terms &amp; Conditions</Text>
                                </Checkbox>

                                <Row gutter={12}>
                                    <Col xs={24} sm={8}>
                                        <Button size="large" block icon={<ArrowLeftOutlined />} onClick={() => { setError(""); setStep(1); }}>
                                            Back
                                        </Button>
                                    </Col>
                                    <Col xs={24} sm={16}>
                                        <Button type="primary" size="large" block loading={loading} disabled={loading || !acceptedTerms} onClick={handleSubmit}>
                                            <CheckCircleOutlined /> Submit Application
                                        </Button>
                                    </Col>
                                </Row>
                            </>
                        )}

                        {/* ── Step 3: Success ── */}
                        {step === 3 && (
                            <Flex vertical align="center" gap="large" style={{ padding: "32px 0", textAlign: "center" }}>
                                <div style={{
                                    width: 96, height: 96, borderRadius: "50%",
                                    background: "#E6F7EF", border: "3px solid #00AF66",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                }}>
                                    <CheckCircleOutlined style={{ fontSize: 48, color: "#00AF66" }} />
                                </div>

                                <div>
                                    <Title level={3} style={{ margin: "0 0 4px" }}>Application Submitted!</Title>
                                    <Title level={5} type="success" style={{ margin: 0 }}>Welcome to the team 🎉</Title>
                                </div>

                                <Card style={{ textAlign: "left", width: "100%" }}>
                                    {[
                                        { icon: <ClockCircleOutlined />, title: "Review in progress",  desc: <>Our team will review your application and documents within <Text strong>5 business days</Text>.</> },
                                        { icon: <MailOutlined />,        title: "Check your email",    desc: <>We'll notify you at <Text strong>{user.email}</Text> once your account has been approved.</> },
                                        { icon: <PhoneOutlined />,       title: "Need help?",          desc: <>Contact us at <Text strong>support@deliveryquickquick.co.za</Text> if you have any questions.</> },
                                    ].map((item, i) => (
                                        <Flex key={i} gap={12} align="flex-start" style={{ marginBottom: i < 2 ? 16 : 0 }}>
                                            <span style={{ color: "#00AF66", fontSize: 20, marginTop: 2 }}>{item.icon}</span>
                                            <div>
                                                <Text strong style={{ display: "block" }}>{item.title}</Text>
                                                <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.7 }}>{item.desc}</Text>
                                            </div>
                                        </Flex>
                                    ))}
                                </Card>

                                <Button type="primary" size="large" block onClick={() => navigate("/logIn")}>
                                    Go to Login
                                </Button>
                            </Flex>
                        )}
                    </Card>

                    {step < 3 && (
                        <Flex gap={4} style={{ marginTop: 20 }}>
                            <Text type="secondary">Already have an account?</Text>
                            <Link onClick={() => navigate("/logIn")}>Sign In</Link>
                        </Flex>
                    )}
                </div>
            </Flex>

            <Footer />
        </>
    );
};

export default DriverRegisterPage;